import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Printer, 
  RefreshCw, 
  Check, 
  X, 
  Zap,
  TestTube,
  Settings
} from "lucide-react";
import { 
  initQzTray, 
  getAvailablePrinters, 
  setDefaultPrinter, 
  getDefaultPrinter,
  printReceiptSilent,
  isQzTrayAvailable 
} from "@/utils/qzPrint";

export const PrinterSettings = () => {
  const [qzConnected, setQzConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(() => getDefaultPrinter() || "");
  const [testPrinting, setTestPrinting] = useState(false);

  const loadPrinters = async () => {
    try {
      const printerList = await getAvailablePrinters();
      setPrinters(printerList || []);
    } catch (error) {
      console.error('Failed to load printers:', error);
      toast.error('Failed to load printer list');
    }
  };

  useEffect(() => {
    const initConnection = async () => {
      setLoading(true);
      try {
        const connected = await isQzTrayAvailable();
        setQzConnected(connected);
        if (connected) {
          await loadPrinters();
        }
      } catch (error) {
        console.error('QZ Tray check failed:', error);
        setQzConnected(false);
      }
      setLoading(false);
    };
    
    initConnection();
  }, []);

  const checkConnection = async () => {
    setLoading(true);
    try {
      const connected = await isQzTrayAvailable();
      setQzConnected(connected);
      if (connected) {
        await loadPrinters();
      }
    } catch (error) {
      console.error('QZ Tray check failed:', error);
      setQzConnected(false);
    }
    setLoading(false);
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const connected = await initQzTray();
      setQzConnected(connected);
      if (connected) {
        toast.success('Connected to QZ Tray!');
        await loadPrinters();
      } else {
        toast.error('Failed to connect to QZ Tray. Is it running?');
      }
    } catch (error) {
      toast.error('Connection failed: ' + error.message);
    }
    setLoading(false);
  };

  const handleSelectPrinter = (printer) => {
    setSelectedPrinter(printer);
    setDefaultPrinter(printer);
    toast.success(`Default printer set to: ${printer}`);
  };

  const handleTestPrint = async () => {
    if (!selectedPrinter) {
      toast.error('Please select a printer first');
      return;
    }

    setTestPrinting(true);
    try {
      const testHtml = `
        <div style="text-align: center; font-family: 'Courier New', monospace; font-weight: bold;">
          <div style="font-size: 18px; margin-bottom: 10px;">*** TEST PRINT ***</div>
          <div style="font-size: 14px;">BOSVELD BOU & HARDEWARE</div>
          <div style="font-size: 14px;">QZ Tray Silent Printing</div>
          <div style="margin: 15px 0; border-top: 1px dashed #000;"></div>
          <div style="font-size: 12px;">If you see this, silent</div>
          <div style="font-size: 12px;">printing is working!</div>
          <div style="margin: 15px 0; border-top: 1px dashed #000;"></div>
          <div style="font-size: 10px;">${new Date().toLocaleString()}</div>
        </div>
      `;

      const result = await printReceiptSilent(testHtml, 1, selectedPrinter);
      
      if (result.success) {
        toast.success('Test print sent successfully!');
      } else {
        toast.error('Print failed: ' + result.error);
      }
    } catch (error) {
      toast.error('Test print failed: ' + error.message);
    }
    setTestPrinting(false);
  };

  return (
    <div className="space-y-6">
      {/* QZ Tray Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            QZ Tray Silent Printing
          </CardTitle>
          <CardDescription>
            QZ Tray enables automatic receipt printing without any dialogs or clicks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Connection Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${qzConnected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="font-medium">
                QZ Tray Status:
              </span>
              <Badge variant={qzConnected ? "default" : "destructive"}>
                {qzConnected ? 'Connected' : 'Not Connected'}
              </Badge>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={qzConnected ? checkConnection : handleConnect}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              {qzConnected ? 'Refresh' : 'Connect'}
            </Button>
          </div>

          {!qzConnected && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-800 mb-2">QZ Tray Not Detected</h4>
              <p className="text-sm text-yellow-700 mb-3">
                Make sure QZ Tray is installed and running on this computer.
              </p>
              <ol className="text-sm text-yellow-700 list-decimal list-inside space-y-1">
                <li>Download QZ Tray from <a href="https://qz.io" target="_blank" rel="noopener noreferrer" className="underline">qz.io</a></li>
                <li>Install and run QZ Tray</li>
                <li>Click &quot;Connect&quot; above</li>
                <li>Allow the connection when prompted</li>
              </ol>
            </div>
          )}

          {qzConnected && (
            <>
              {/* Printer Selection */}
              <div className="space-y-2">
                <Label>Select Receipt Printer</Label>
                <Select value={selectedPrinter} onValueChange={handleSelectPrinter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose your thermal printer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {[...printers].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((printer) => (
                      <SelectItem key={printer} value={printer}>
                        <div className="flex items-center gap-2">
                          <Printer className="h-4 w-4" />
                          {printer}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500">
                  Found {printers.length} printer(s). Select your 80mm thermal receipt printer.
                </p>
              </div>

              {/* Test Print Button */}
              {selectedPrinter && (
                <div className="pt-4">
                  <Button 
                    onClick={handleTestPrint} 
                    disabled={testPrinting}
                    className="w-full"
                  >
                    {testPrinting ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Printing...
                      </>
                    ) : (
                      <>
                        <TestTube className="h-4 w-4 mr-2" />
                        Test Print (1 copy)
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Success Message */}
              {selectedPrinter && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-800">
                    <Check className="h-5 w-5" />
                    <span className="font-medium">Silent Printing Ready!</span>
                  </div>
                  <p className="text-sm text-green-700 mt-1">
                    Receipts will automatically print to <strong>{selectedPrinter}</strong> when a sale is completed. No clicks needed!
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            How It Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3 text-sm">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-800 rounded-full flex items-center justify-center font-bold">1</span>
              <span>QZ Tray runs in the background on the cashier&apos;s computer</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-800 rounded-full flex items-center justify-center font-bold">2</span>
              <span>When you complete a sale, the POS sends the receipt directly to QZ Tray</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-800 rounded-full flex items-center justify-center font-bold">3</span>
              <span>QZ Tray prints 2 copies (customer + store) silently - no dialog!</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-800 rounded-full flex items-center justify-center font-bold">4</span>
              <span>The screen automatically returns to the sales page for the next customer</span>
            </li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default PrinterSettings;
